import { Button, Loading } from "@mozioinc/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { isIOS, isMacOs, isSafari, isMobileSafari } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { GeneralError } from "../components";
import { appleWalletQuery } from "../utils/query";
import type { TransNamespace } from "../types";
import type { AppleWalletPass } from "@mozioinc/query";

// Safari (desktop and iOS) hands a recognized .pkpass blob straight to
// Wallet when it's just navigated to, but treats it as a plain file save
// if `download` is set — so only set it for other browsers, which have no
// such handoff and would otherwise download an extensionless file.
const triggerOpen = (pass: AppleWalletPass) => {
  const url = URL.createObjectURL(pass.blob);
  const link = document.createElement("a");
  link.href = url;
  if (!isSafari && !isMobileSafari) {
    link.download = pass.filename;
  }
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const ReservationsAppleWalletPage = ({ hashedId }: { hashedId: string }) => {
  const { t } = useTranslation<TransNamespace>("transfer:apple-wallet");
  const isApplePlatform = isIOS || isMacOs;

  const { data, isPending, isError } = useQuery({
    queryKey: ["reservation-apple-wallet", hashedId],
    queryFn: () => appleWalletQuery.getReservationApplePass(hashedId),
    enabled: isApplePlatform,
  });

  const hasOpened = useRef(false);

  useEffect(() => {
    if (data && !hasOpened.current) {
      hasOpened.current = true;
      triggerOpen(data);
    }
  }, [data]);

  if (!isApplePlatform) {
    return (
      <div className="grid min-h-screen place-items-center px-4 text-center">
        <div className="flex max-w-md flex-col items-center gap-4">
          <h1 className="text-2xl font-semibold">
            {t("This link only works on Apple devices")}
          </h1>
          <p className="text-neutral-700">
            {t(
              "Open this email on an iPhone, iPad, or Mac to add your reservation to Apple Wallet.",
            )}
          </p>
        </div>
      </div>
    );
  }

  if (isPending) {
    return <Loading className="h-screen" />;
  }

  if (isError || !data) {
    return <GeneralError className="h-screen" />;
  }

  return (
    <div className="grid min-h-screen place-items-center px-4 text-center">
      <div className="flex max-w-md flex-col items-center gap-4">
        <h1 className="text-2xl font-semibold">
          {t("Adding your reservation to Apple Wallet")}
        </h1>
        <p className="text-neutral-700">
          {t(
            "If Apple Wallet did not open automatically, use the button below.",
          )}
        </p>
        <Button onClick={() => triggerOpen(data)}>
          {t("Add to Apple Wallet")}
        </Button>
        <p className="text-sm text-neutral-500">
          {t("You can close this window once your pass has been added.")}
        </p>
      </div>
    </div>
  );
};

export default ReservationsAppleWalletPage;
